import { QueryResponse, DataFrame } from '../types/grafana';
import { normalizeLogLevel } from './logUtils';

export const transformGrafanaResponse = (response: QueryResponse): any[] => {
  const resultMap = new Map<number, any>();
  
  if (!response?.results) return [];
  
  Object.values(response.results).forEach((resultObj: any) => {
    if (!resultObj.frames) return;
    
    resultObj.frames.forEach((frame: DataFrame) => {
      if (!frame.data?.values) return;
      
      const isLokiLogs = frame.schema?.fields?.some(f => f.name === 'labels' || f.name === 'Line');

      if (isLokiLogs) {
        const timestamps = frame.data.values[1]; // Índice 1 contém os timestamps
        const lines = frame.data.values[2]; // Índice 2 contém as linhas de log
        const labels = frame.data.values[0]; // Índice 0 contém os labels
        
        if (!timestamps || !lines) return;
        
        for (let i = 0; i < timestamps.length; i++) {
          const originalTimestamp = new Date(timestamps[i]).getTime();
          const formattedTimestamp = new Date(timestamps[i]).toLocaleTimeString();
          
          let rawLevel = 'info';
          if (labels && labels[i] && typeof labels[i] === 'object') {
            rawLevel = labels[i].level || 'info';
          }

          const level = normalizeLogLevel(rawLevel, lines[i]);
          
          const existingPoint = resultMap.get(originalTimestamp);
          if (existingPoint) {
            existingPoint[level] = (existingPoint[level] || 0) + 1;
          } else {
            const point: any = { 
              timestamp: formattedTimestamp,
              _originalTimestamp: originalTimestamp
            };
            point[level] = 1;
            resultMap.set(originalTimestamp, point);
          }
        }
      } else if (frame.data.values.length >= 2) {
        const timestamps = frame.data.values[0];
        const values = frame.data.values.slice(1);
        const labels = frame.schema.fields.slice(1).map((f: any) => f.name);
        
        for (let i = 0; i < timestamps.length; i++) {
          const originalTimestamp = new Date(timestamps[i]).getTime();
          const formattedTimestamp = new Date(timestamps[i]).toLocaleTimeString();
          
          const point: any = {
            timestamp: formattedTimestamp,
            _originalTimestamp: originalTimestamp
          };

          for (let j = 0; j < values.length; j++) {
            const value = values[j][i];
            point[labels[j]] = typeof value === 'object' ? JSON.stringify(value) : value;
          }
          
          resultMap.set(originalTimestamp, point);
        }
      }
    });
  });
  
  const result = Array.from(resultMap.values());
  result.sort((a, b) => a._originalTimestamp - b._originalTimestamp);
  
  result.forEach(point => {
    delete point._originalTimestamp;
  });
  
  return result;
};

export const transformGrafanaTableResponse = (response: QueryResponse): { columns: any[], rows: any[] } => {
  const columns: any[] = [];
  const rows: any[] = [];
  
  if (!response?.results) return { columns, rows };
  
  Object.values(response.results).forEach((resultObj: any) => {
    if (!resultObj.frames) return;
    
    resultObj.frames.forEach((frame: DataFrame) => {
      if (!frame.data?.values || !frame.schema?.fields) return;
      
      const isLokiLogs = frame.schema.fields.some(f => f.name === 'labels' || f.name === 'Line');

      if (isLokiLogs) {
        const timestamps = frame.data.values[1];
        const lines = frame.data.values[2];
        const labels = frame.data.values[0];
        const tsNs = frame.data.values[3];
        
        if (!timestamps || !lines) return;
        
        if (columns.length === 0) {
          columns.push(
            { title: 'Timestamp', field: 'timestamp' },
            { title: 'Level', field: 'level' },
            { title: 'Message', field: 'message' }
          );
        }
        
        const tempRows = [];
        for (let i = 0; i < timestamps.length; i++) {
          const timestamp = new Date(timestamps[i]).toLocaleString();
          const originalTimestamp = new Date(timestamps[i]).getTime();
          let rawLevel = 'info';
          let labelObj = {};

          if (labels && labels[i] && typeof labels[i] === 'object') {
            rawLevel = labels[i].level || 'info';
            labelObj = labels[i];
          }
          
          const level = normalizeLogLevel(rawLevel, lines[i]);
          
          tempRows.push({
            timestamp,
            level,
            message: lines[i],
            labels: labelObj,
            tsNs: tsNs ? tsNs[i] : null,
            _originalTimestamp: originalTimestamp
          });
        }
        
        tempRows.sort((a, b) => a._originalTimestamp - b._originalTimestamp);

        tempRows.forEach(({ _originalTimestamp: _ignored, ...row }) => {
          rows.push(row);
        });
      } else {
        const tableColumns = frame.schema.fields.map((field: any) => ({
          title: field.name,
          field: field.name,
        }));
        
        if (columns.length === 0) {
          columns.push(...tableColumns);
        }
        
        const values = frame.data.values;
        const rowCount = values[0]?.length || 0;
        const tempRows = [];
        
        for (let i = 0; i < rowCount; i++) {
          const row: any = {};
          let originalTimestamp = 0;
          
          for (let j = 0; j < values.length; j++) {
            const columnName = frame.schema.fields[j].name;
            const value = values[j][i];
            
            if (columnName.toLowerCase().includes('time')) {
              try {
                originalTimestamp = new Date(value).getTime();
              } catch (e) {
                // intentionally ignored
              }
            }
            
            if (typeof value === 'object' && value !== null) {
              row[columnName] = value;
              row[`${columnName}_display`] = JSON.stringify(value);
            } else {
              row[columnName] = value;
            }
          }
          
          row._originalTimestamp = originalTimestamp;
          tempRows.push(row);
        }
        
        tempRows.sort((a, b) => a._originalTimestamp - b._originalTimestamp);

        tempRows.forEach(({ _originalTimestamp: _ignored, ...row }) => {
          rows.push(row);
        });
      }
    });
  });
  
  return { columns, rows };
};